/**
 * Unit detail rows — relay controller identity, radio status, last-heard,
 * and signal for the site dashboard rail.
 *
 * Reads device data from the new odl-app `unit` contract (plain JS object
 * from the siteDashboard slice). The `radio` sub-object is accessed directly
 * from `unit.radio` without Immutable.js.
 *
 * Contains:
 * - UnitDetailsList — detail row list (default export, connect-wrapped)
 */
import { Capacitor } from '@capacitor/core';
import { CancelOutlined, CheckCircleOutlined, InfoOutlined } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Immutable from 'immutable';
import moment from 'moment-timezone';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import BluetoothIndicator from '@sophocles/components/bluetooth/BluetoothIndicator';
import SignalBars from '@sophocles/components/SignalStrength/SignalBars';
import { selectCapabilities } from '@sophocles/features/capabilities';
import { selectSiteFeatureAssociationByKey } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import { SMARTLINK_MODEL } from '@sophocles/utils/const';
import { formatToMAC } from '@sophocles/utils/formatUtils';
import { isCellular, isEthernet, isWiFi } from '@sophocles/utils/radioUtils';
import DetailRow from '@sophocles/views/sites/components/DetailRow';
import HardwareHistoryDialog from '@sophocles/views/sites/components/UnitCard/HardwareHistoryDialog';

/**
 * Convert the new contract's `comm_modes` object ({cellular, ethernet, wifi})
 * to the numeric bitmask the radioUtils helpers expect:
 *   bit 0 (0b001) = cellular, bit 1 (0b010) = ethernet, bit 2 (0b100) = wifi.
 * Falls back to 0 (cellular-only assumption) when already a number or absent.
 *
 * @param {object|number|null|undefined} commModes
 * @returns {number}
 */
function normalizeCommModesBitmask(commModes) {
  if (commModes == null) return 0;
  if (typeof commModes === 'number') return commModes;
  return (commModes.cellular ? 0b001 : 0) | (commModes.ethernet ? 0b010 : 0) | (commModes.wifi ? 0b100 : 0);
}

/**
 * Build a plain-JS radio object compatible with radioUtils (numeric comm_modes,
 * legacy field names) from the new contract's radio sub-object.
 *
 * @param {object|null} contractRadio - unit.radio from the new slice
 * @returns {object|null}
 */
function normalizeRadio(contractRadio) {
  if (contractRadio == null) return null;
  return {
    id: contractRadio.id ?? null,
    iccid: contractRadio.iccid ?? null,
    mac_address: contractRadio.mac_address ?? null,
    ip_address: contractRadio.ip_address ?? null,
    public_ip_address: contractRadio.public_ip_address ?? null,
    local_ip_address: contractRadio.local_ip_address ?? null,
    port: contractRadio.port ?? null,
    online: contractRadio.online ?? false,
    // signal_strength is the new name; radioUtils + SignalBars consume last_known_signal_strength.
    last_known_signal_strength: contractRadio.signal_strength ?? null,
    kore_status: contractRadio.kore_status ?? null,
    // Normalize comm_modes to a bitmask for radioUtils.
    comm_modes: normalizeCommModesBitmask(contractRadio.comm_modes),
    carrier: contractRadio.carrier ?? null,
  };
}

class UnitDetailsList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showWaiting: false,
      elapsedTime: 0,
      waitingTimeout: 0,
      hardwareHistoryOpen: false,
    };
  }

  componentDidMount() {
    this._processWaitingTime(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this._processWaitingTime(nextProps);
  }

  openHardwareHistory = () => this.setState({ hardwareHistoryOpen: true });
  closeHardwareHistory = () => this.setState({ hardwareHistoryOpen: false });

  _hideWaitingAfterFiveMinutes = diffTime =>
    setTimeout(
      () =>
        this.setState({
          showWaiting: false,
        }),
      diffTime * 1000,
    );

  _processWaitingTime = props => {
    const { siteId, completedCommands } = props;

    const { waitingTimeout } = this.state;

    const siteIdStr = String(siteId ?? '0');
    const waitForCommands = completedCommands.get(siteIdStr, Immutable.List()).filter(command => {
      const c = command.get('command', 'none');
      return !!(c == 'STATUS_GET_SHORT' || c == 'STATUS_GET_LIVE_POWER');
    });
    const lastCompleted = waitForCommands.first() ? waitForCommands.first() : Immutable.Map();
    const sentTime = moment(lastCompleted.get('processed_time', moment().subtract(10, 'minutes')));
    const fiveMinutesAgo = moment().subtract(5, 'minutes');

    if (lastCompleted.get('acknowledged_time', null) == null && sentTime.isAfter(fiveMinutesAgo)) {
      const diffTime = sentTime.diff(fiveMinutesAgo, 'seconds');
      const elapsedTime = 5 * 60 - diffTime;

      clearTimeout(waitingTimeout);
      this.setState({
        elapsedTime: elapsedTime,
        showWaiting: true,
        waitingTimeout: this._hideWaitingAfterFiveMinutes(diffTime),
      });
    } else {
      clearTimeout(this.state.waitingTimeout);
      this.setState({
        showWaiting: false,
      });
    }
  };

  onHardwareHistoryClick = url => {
    const { router } = this.props;
    router.navigate(url);
  };

  buildUnitName = (unit, cic, isBillboard, meteringEnabled, meterType) => {
    let unitName = '';

    if (unit) {
      unitName = unit.model?.display_name ?? 'Generic Unit';

      const modelId = unit.model?.id ?? 0;
      const alwaysCommercialModels = [
        SMARTLINK_MODEL.SL_TWO_PORT_DC, // 15
        SMARTLINK_MODEL.VANTAGE, // 16
        SMARTLINK_MODEL.ODL_1_SERIES_AC, // 17
        SMARTLINK_MODEL.ODL_2_SERIES_AC, // 18
      ];
      if (!alwaysCommercialModels.includes(modelId) && unit.uses_zoneinfo_status) {
        unitName += ' (Commercial)';
      }

      if (meteringEnabled) {
        switch (meterType) {
          case 0:
            unitName += ' with Solar Meter';
            break;
          case 1:
            unitName += ' with ANSI Meter';
            break;
        }
      }
    } else if (cic) {
      unitName = 'OutdoorLink Secure';
    } else {
      unitName = null;
    }

    if (!unitName) {
      return null;
    }

    return unitName;
  };

  getIdentifier = radio => {
    if (radio == null) {
      return null;
    }

    if (isCellular(radio)) {
      return radio.iccid;
    }

    return formatToMAC(radio.mac_address);
  };

  render() {
    const {
      unit,
      site,
      siteId,
      completedCommands,
      isBillboard,
      selectFeatureAssociation,
      isStaff,
      isEngineering,
      canManageUnit,
      params,
      t,
    } = this.props;

    const { showWaiting, hardwareHistoryOpen } = this.state;
    const isNativePlatform = Capacitor.isNativePlatform();

    // Device configuration lives on its own page (DeviceConfigurationView),
    // gated to ODL staff / engineering — except on 2PDC units, where the
    // page's thresholds/status_requests sections accept the wider legacy
    // audience (at-least-site-group-admin), so the entry point shows for
    // canManageUnit there too. Device/network/cic stay staff-only on the
    // page itself regardless of who reaches it.
    const is2PDC = unit?.model?.id === SMARTLINK_MODEL.SL_TWO_PORT_DC;
    const canConfigureDevice = isStaff || isEngineering || (canManageUnit && is2PDC);
    const configurationPath =
      params?.cust_id != null
        ? `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/configuration`
        : null;

    const waitingComponent = showWaiting ? <CircularProgress /> : null;

    // The unit is the relay controller; cic is the addon camera interface.
    const cic = unit?.cic ?? null;
    const radio = normalizeRadio(unit?.radio ?? null);

    const radioId = radio?.id ?? null;
    const iccid = radio?.iccid ?? null;
    const macAddress = radio?.mac_address ?? null;
    const radioIdentifier = iccid || (macAddress ? formatToMAC(macAddress) : t('sites.noRadio'));
    const online = radio?.online ?? null;
    const signal = radio?.last_known_signal_strength ?? null;
    const koreStatus = radio?.kore_status ?? null;

    // time_zone comes from the site contract; fall back to Chicago.
    const timezone = site?.time_zone ?? 'America/Chicago';
    const lastActive = unit?.last_active ?? null;
    const time = lastActive ? moment.tz(lastActive, 'Etc/GMT').tz(timezone) : null;

    const fromNowTime = time && time.isValid() ? time.fromNow() : t('general.never');
    const fullTime = time && time.isValid() ? time.format('L, LTS') : t('general.never');

    const statusComponent = (
      <span>
        <span title={fullTime}>{fromNowTime}</span>
        {showWaiting ? <span>{t('sites.waitingForResponse')}</span> : null}
      </span>
    );

    const effectiveSiteId = siteId ?? 0;
    const meteringFeature = selectFeatureAssociation(effectiveSiteId, 'METERING');
    const meteringEnabled = meteringFeature?.is_enabled ?? false;

    const unitName = this.buildUnitName(unit, cic, isBillboard, meteringEnabled, unit?.configuration?.power_meter_type);

    const currentUpgrade = unit?.current_upgrade ?? null;
    const upgradeProgressPct =
      currentUpgrade && (currentUpgrade.end_segment || 0) > 0
        ? Math.round(((currentUpgrade.programming_segment || 0) / currentUpgrade.end_segment) * 100)
        : 0;
    // TODO(odl-app-contract): can_upgrade_firmware is not in the BE capabilities
    // contract (not in the /me capabilities block or meta.permissions). Needs a
    // BE add before this can be replaced. Using is_staff as the gate until then.
    const canViewUpgrade = this.props.capabilities?.is_staff ?? false;
    const upgradeLinkProps =
      currentUpgrade && canViewUpgrade
        ? {
            component: Link,
            to: `/staff/firmware-upgrades/${currentUpgrade.batch_id}`,
            style: { textDecoration: 'none', color: 'inherit', display: 'inline-flex', alignItems: 'center' },
          }
        : { style: { display: 'inline-flex', alignItems: 'center' } };
    const upgradeDisplay = currentUpgrade ? (
      <Box component="span" {...upgradeLinkProps} sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
        <UpgradeIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.75, mr: 0.25 }} />
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {t('sites.upgradingPct', {
            pct: upgradeProgressPct,
            defaultValue: '(Upgrading {{pct}}%)',
          })}
        </Typography>
      </Box>
    ) : null;

    const radioDetailsTooltip =
      radio != null && isStaff ? (
        <div style={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
          <div>
            <strong>{t('general.status', 'Status')}:</strong> {koreStatus}
          </div>
          <div>
            <strong>{t('sites.cellularIp', 'Cellular IP')}:</strong> {radio.ip_address ?? '—'}
          </div>
          <div>
            <strong>{t('sites.publicIp', 'Public IP')}:</strong> {radio.public_ip_address ?? '—'}
          </div>
          <div>
            <strong>{t('sites.localIp', 'Local IP')}:</strong> {radio.local_ip_address ?? '—'}
          </div>
          <div>
            <strong>{t('sites.port', 'Port')}:</strong> {radio.port ?? '—'}
          </div>
          {isWiFi(radio) ? (
            <div>
              <strong>{t('sites.router', 'Router')}:</strong> {radio.iccid ?? '—'}
            </div>
          ) : null}
          {iccid && macAddress ? (
            <div>
              <strong>{t('sites.mac', 'MAC')}:</strong> {formatToMAC(macAddress)}
            </div>
          ) : null}
        </div>
      ) : (
        koreStatus || ''
      );

    // Treat null / unknown kore_status as active — only 'Inactive' / 'Pending'
    // represent a meaningful non-active state. A null status should not spin
    // indefinitely as if something is in progress.
    const radioStatusIcon =
      radio != null ? (
        koreStatus === 'Active' ||
        koreStatus === 'Test' ||
        koreStatus === 'Ready' ||
        koreStatus == null ||
        isWiFi(radio) ||
        isEthernet(radio) ? (
          <CheckCircleOutlined fontSize="small" />
        ) : koreStatus === 'Inactive' ? (
          <CancelOutlined fontSize="small" />
        ) : koreStatus === 'Pending' ? (
          <InfoOutlined fontSize="small" />
        ) : (
          // Genuinely in-progress state (e.g. provisioning).
          <CircularProgress style={{ width: '1em', height: '1em', fontSize: '14px' }} />
        )
      ) : null;

    const radioIdentity = (
      <Tooltip title={radioDetailsTooltip}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            color:
              koreStatus === 'Active' ||
              koreStatus === 'Ready' ||
              koreStatus == null ||
              isWiFi(radio) ||
              isEthernet(radio)
                ? 'success.main'
                : koreStatus === 'Inactive'
                  ? 'error.main'
                  : koreStatus === 'Pending'
                    ? 'warning.main'
                    : 'text.secondary',
          }}
        >
          <Typography
            component="span"
            noWrap
            title={radioIdentifier}
            sx={{
              display: 'block',
              minWidth: 0,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {radioIdentifier}
          </Typography>
          {radioStatusIcon}
        </Box>
      </Tooltip>
    );

    const swapAction =
      this.props.canChangeUnit && this.props.openSwapDialog != null ? (
        <Button
          type="button"
          size="small"
          variant="outlined"
          aria-label={t('sites.swapDevice', 'Swap Device')}
          onClick={() => this.props.openSwapDialog()}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            minWidth: 36,
            width: 36,
            height: 32,
            p: 0,
          }}
        >
          <SwapHorizIcon fontSize="small" />
        </Button>
      ) : null;

    const iccidActions = (
      <Stack direction="row" alignItems="center" sx={{ mt: 0.25, ml: -0.625 }}>
        {radio ? (
          <Tooltip title={t('actions.copy', 'Copy')}>
            <IconButton
              size="small"
              onClick={() => {
                const value = radioIdentifier;
                navigator.clipboard.writeText(value);
                this.props.addToast({
                  title: t('actions.copied', 'Copied'),
                  message: t('sites.deviceIdentifierCopied', 'Device identifier copied to clipboard.'),
                  level: 'success',
                  dismissible: true,
                });
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {isStaff && !isNativePlatform && radioId != null ? (
          <Tooltip title={t('search.radioDetails', 'Radio details')}>
            <IconButton size="small" component={Link} to={`/support/radios/${radioId}`}>
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {isStaff ? (
          <Tooltip title={t('general.history')}>
            <IconButton size="small" onClick={this.openHardwareHistory}>
              <HistoryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {canConfigureDevice && configurationPath ? (
          <Tooltip title={t('sites.deviceConfiguration', 'Device configuration')}>
            <IconButton
              size="small"
              component={Link}
              to={configurationPath}
              aria-label={t('sites.deviceConfiguration', 'Device configuration')}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    );

    return (
      <Box sx={{ px: 'var(--space-3)', py: 'var(--space-2)' }}>
        <Stack spacing={1.25} sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0, position: 'relative', pr: swapAction ? 5 : 0 }}>
            {swapAction}
            {unit ? (
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.35, minWidth: 0 }}
                >
                  {unitName}
                </Typography>
                {upgradeDisplay}
              </Box>
            ) : null}
            <Box sx={{ minWidth: 0, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box sx={{ minWidth: 0, flexShrink: 1 }}>{radioIdentity}</Box>
                {iccid && isCellular(radio) ? (
                  <BluetoothIndicator iccid={iccid} fontSize="small" sx={{ flexShrink: 0 }} />
                ) : null}
              </Box>
              {iccidActions}
            </Box>
          </Box>

          {unit !== null ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                gap: 'var(--space-2)',
              }}
            >
              <DetailRow
                dense
                label={t('sites.lastComm')}
                value={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    {statusComponent}
                    {waitingComponent}
                  </Box>
                }
              />
              <DetailRow
                dense
                align="right"
                label={t('sites.signal', 'Signal')}
                value={<SignalBars value={signal != null ? Number(signal) : null} online={!!online} />}
              />
            </Box>
          ) : null}
        </Stack>

        {isStaff && hardwareHistoryOpen && (
          <HardwareHistoryDialog
            isStaff={isStaff}
            siteId={effectiveSiteId}
            open={true}
            closeDialog={this.closeHardwareHistory}
            onRadioIdClick={this.onHardwareHistoryClick}
          />
        )}
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    // capabilities replaces getAuthUserSelector for is_staff / upgrade gate.
    capabilities: selectCapabilities(state),
    selectFeatureAssociation: (siteId, featureKey) => selectSiteFeatureAssociationByKey(siteId, featureKey)(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    addToast: toast => dispatch(addToast(toast)),
  };
}

const UnitDetailsListContainer = connect(mapStateToProps, mapDispatchToProps)(UnitDetailsList);

export default withTranslation()(UnitDetailsListContainer);
